import React, { useState, useEffect } from "react";
import {
  GelBoxLayout,
  GelButton,
  GelContainerLite,
  GelParagraph,
  GelLabel,
  GelForm,
  GelFormField,
  GelNumberInput,
  useGelFormData,
  GelSpinner,
  GelCaption,
} from "@tal-gel/components";
import { useNavigate, useSearchParams } from "react-router-dom";
import API from "../constants/constants";
import { useCookies } from "react-cookie";
import axios, { AxiosResponse, AxiosError } from "axios";
import { getGelTokens } from "@tal-gel/theming";
import { VALIDATE_MFA_QUERY, RESEND_MFA } from "../graphql/graphql-bff";
import { GET_EFTPAGE_CONTENT_QUERY } from "../graphql/graphql-contentful";
import Header from "./common/header";
import Footer from "./common/footer";
import { getMicrositeParams } from "../helpers/helpers";

const VerificationPage = () => {
  const [contentfulData, setcontenfulData] = useState<null | {
    mmcEftPageCollection: any;
  }>(null);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDisabled, setIsDisabled] = useState(true);
  const [userName, setUserName] = useState("");
  const [memberNumber, setMemberNumber] = useState("");
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [resendError, setResendError] = useState(false);

  const [ssoCookies] = useCookies(["aid", "rid", "ssoid", "tpid", "sid"]);

  const { formData, onFormDataChange, setFormData } = useGelFormData({
    passcode: "",
  });

  const onSubmit = () => {
    setIsFormDisabled(true);

    const variables = {
      passCodeValidationRequest: {
        userName: userName,
        memberNumber: memberNumber,
        passcode: formData.passcode,
        source: "EFT",
      },
    };

    axios
      .post(
        API.BASEURL,
        {
          query: VALIDATE_MFA_QUERY,
          variables: variables,
        },
        {
          headers: {
            "x-aid": ssoCookies.aid,
            "x-rid": ssoCookies.rid,
            ssoid: ssoCookies.ssoid,
            tpid: ssoCookies.tpid,
            sid: ssoCookies.sid,
          },
        }
      )
      .then((response: AxiosResponse<any>) => {
        navigate("/eft-form", {
          state: {
            auth: true,
          },
        });
      })
      .catch((error: AxiosError) => {
        if (error.response?.status === 401) {
          navigate("/error");
        } else {
          setIsFormDisabled(false);
          setIsError(true);
          setErrorMessage(
            contentfulData?.mmcEftPageCollection.items[0]?.errorMessageContent
              .invalidPasscode
          );
        }
      });
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.value === "") {
      setIsError(false);
      setErrorMessage("");
      setIsDisabled(true);
    } else if (e.target.value.length < 6) {
      setIsError(true);
      setIsDisabled(true);
      setErrorMessage(
        contentfulData?.mmcEftPageCollection.items[0]?.errorMessageContent
          .passcodeMinLength
      );
    } else {
      setIsDisabled(false);
      setIsError(false);
      setErrorMessage("");
    }
  };

  useEffect(() => {
    setIsLoading(true);

    //contentful call
    axios
      .post(API.CONTENTFUL_URL, {
        query: GET_EFTPAGE_CONTENT_QUERY,
      })
      .then(async (response) => {
        setcontenfulData(response.data?.data);
      });

    const { origin, userName, claimId, requirementId, memberNumber } = getMicrositeParams();

    setUserName(userName);
    setMemberNumber(memberNumber);

    if (claimId && requirementId && origin) {
      sessionStorage.setItem("claimId", claimId);
      sessionStorage.setItem("requirementId", requirementId);
      sessionStorage.setItem("origin", origin);
    }

    setIsLoading(false);
  }, []);

  const onClickResend = () => {
    const requestData = {
      userName: userName,
      memberNumber: memberNumber,
      source: "EFT",
    };

    axios
      .post(
        API.BASEURL,
        {
          query: RESEND_MFA,
          variables: {
            mfaConfigRequest: requestData,
          },
        },
        {
          headers: {
            "x-aid": ssoCookies.aid,
            "x-rid": ssoCookies.rid,
            ssoid: ssoCookies.ssoid,
            tpid: ssoCookies.tpid,
            sid: ssoCookies.sid,
          },
        }
      )
      .then(async (response) => {
        setResendError(false);
      })
      .catch((error: AxiosError) => {
        console.log(error);
        setResendError(true);
      });
  };

  return (
    <div>
      <Header
        title={contentfulData?.mmcEftPageCollection.items[0]?.mfaPageTitle}
      />
      <GelContainerLite>
        {isLoading && <GelSpinner large overlay />}
        <GelBoxLayout
          gap="medium"
          alignment="start"
          space="auto"
          distribution="spaceBetween"
          vertical="true"
          padding="large"
          margin="large"
          style={{
            marginTop: `-${getGelTokens().global.sizeBaseX15}px`,
            backgroundColor: getGelTokens().global.themeColorWhite,
            border: `${getGelTokens().global.borderSizeSm}px solid`,
            borderColor: getGelTokens().global.themeColorGrayT20,
          }}
        >
          <GelParagraph>
            {contentfulData?.mmcEftPageCollection.items[0]?.mfaDescription}
          </GelParagraph>
          <GelForm labelAtTop disabled={isFormDisabled} onSubmit={onSubmit}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                paddingBottom: getGelTokens().global.sizeBaseX2,
              }}
            >
              <GelLabel marginRight="small">
                {contentfulData?.mmcEftPageCollection.items[0]?.mfaFormLabel}
              </GelLabel>
            </div>
            <GelFormField>
              <GelNumberInput
                name="passcode"
                defaultValue={formData.passcode}
                onChange={onChange}
                error={isError}
                noFormat
                integer
                maxLength="6"
                required
                requiredErrorMsg={
                  contentfulData?.mmcEftPageCollection.items[0]
                    ?.errorMessageContent.required
                }
              />
              {isError && (
                <GelBoxLayout marginTop="small">
                  <GelCaption
                    condensed
                    style={{
                      color: getGelTokens().global.themeColorDanger,
                    }}
                  >
                    {errorMessage}
                  </GelCaption>
                </GelBoxLayout>
              )}
              {resendError && (
                <GelBoxLayout marginTop="small">
                  <GelCaption
                    condensed
                    style={{
                      color: getGelTokens().global.themeColorDanger,
                    }}
                  >
                    Resend failed
                  </GelCaption>
                </GelBoxLayout>
              )}
            </GelFormField>

            <GelFormField>
              <GelButton
                name="" // Makes it a managed component
                tertiary
                small
                onClick={onClickResend}
              >
                {
                  contentfulData?.mmcEftPageCollection.items[0]
                    ?.mfaResendButtonText
                }
              </GelButton>
            </GelFormField>

            <GelBoxLayout alignment="start" gap="small">
              <GelFormField>
                <GelButton
                  name="" // Makes it a managed component
                  primary
                  medium
                  submit
                  onSubmit={onSubmit}
                  disabled={isDisabled}
                >
                  {
                    contentfulData?.mmcEftPageCollection.items[0]
                      ?.mfaSubmitButtonText
                  }
                </GelButton>
              </GelFormField>
            </GelBoxLayout>
          </GelForm>
        </GelBoxLayout>
      </GelContainerLite>
      <Footer contentfulData={contentfulData} />
    </div>
  );
};

export default VerificationPage;
